(() => {
  // Native Stretch uses pointer:fine hover only. This touch enhancement leaves
  // its links, quick buy, horizontal rails and desktop behavior intact.
  if (!window.matchMedia || typeof IntersectionObserver === 'undefined') return;
  const touch = window.matchMedia('(hover: none) and (pointer: coarse)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  const enabled = () => touch.matches && !reduced.matches;
  const pauseIcon = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M7 5v10M13 5v10" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
  const playIcon = '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="m7 4 9 6-9 6Z" fill="currentColor"/></svg>';
  const ready = state => [state.primary, state.secondary].every(image => image.complete && image.naturalWidth > 0);
  function cancel(state) { window.clearTimeout(state.timer); state.timer = null; }
  function paint(state) {
    const active = enabled() && ready(state);
    state.card.toggleAttribute('data-daelivo-photo-motion', active);
    if (!active || !state.visible) state.card.removeAttribute('data-daelivo-photo-secondary');
    state.button.hidden = !active;
    if (state.button.getAttribute('aria-pressed') !== String(state.paused)) {
      state.button.setAttribute('aria-label', state.paused ? 'Play product photos' : 'Pause product photos');
      state.button.setAttribute('aria-pressed', String(state.paused));
      state.button.innerHTML = state.paused ? playIcon : pauseIcon;
    }
  }
  function arm(state) {
    cancel(state); paint(state);
    if (!state.card.isConnected || !enabled() || !ready(state) || !state.visible || state.paused || state.held || state.focused || document.hidden) return;
    state.timer = window.setTimeout(() => {
      state.timer = null;
      if (enabled() && ready(state) && state.visible && !state.paused && !state.held && !state.focused && !document.hidden && state.card.isConnected) {
        state.card.toggleAttribute('data-daelivo-photo-secondary', !state.card.hasAttribute('data-daelivo-photo-secondary'));
      }
      arm(state);
    }, 4000);
  }
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = [...states.values()].find(item => item.figure === entry.target);
      if (!state) continue;
      state.visible = entry.isIntersecting && entry.intersectionRatio >= .55;
      if (state.visible && state.secondary.loading === 'lazy') state.secondary.loading = 'eager';
      arm(state);
    }
  }, { threshold: [0, .55] });
  function remove(state) {
    cancel(state); observer.unobserve(state.figure);
    for (const [node, type, fn] of state.listeners) node.removeEventListener(type, fn);
    state.button.remove(); state.card.removeAttribute('data-daelivo-photo-motion'); state.card.removeAttribute('data-daelivo-photo-secondary');
    states.delete(state.card);
  }
  function add(card) {
    if (!enabled() || !card.closest('main') || states.has(card)) return;
    const figure = card.querySelector('.product-card__figure'), primary = card.querySelector('.product-card__image--primary'), secondary = card.querySelector('.product-card__image--secondary');
    if (!figure || !primary || !secondary) return;
    const button = document.createElement('button'); button.type = 'button'; button.className = 'daelivo-card-photo-toggle';
    const state = { card, figure, primary, secondary, button, visible: false, paused: false, held: false, focused: false, timer: null, listeners: [] };
    const listen = (node, type, fn) => { node.addEventListener(type, fn, { passive: type.startsWith('pointer') }); state.listeners.push([node, type, fn]); };
    listen(button, 'click', event => { event.preventDefault?.(); event.stopPropagation?.(); state.paused = !state.paused; arm(state); });
    listen(card, 'pointerdown', () => { state.held = true; cancel(state); });
    for (const type of ['pointerup', 'pointercancel']) listen(card, type, () => { state.held = false; arm(state); });
    listen(card, 'focusin', event => { if (event.target !== button) { state.focused = true; cancel(state); } });
    listen(card, 'focusout', event => { if (!card.contains(event.relatedTarget) || event.relatedTarget === button) { state.focused = false; arm(state); } });
    for (const image of [primary, secondary]) for (const type of ['load', 'error']) listen(image, type, () => arm(state));
    states.set(card, state); figure.append(button); paint(state); observer.observe(figure);
  }
  function scan(scope = document) {
    for (const state of states.values()) {
      if (!state.card.isConnected || state.primary !== state.card.querySelector('.product-card__image--primary') || state.secondary !== state.card.querySelector('.product-card__image--secondary')) remove(state);
    }
    if (scope.matches?.('product-card')) add(scope);
    const parentCard = scope.closest?.('product-card'); if (parentCard) add(parentCard);
    for (const card of scope.querySelectorAll?.('product-card') || []) add(card);
  }
  function sync() {
    scan();
    for (const state of states.values()) {
      if (!enabled()) { state.visible = false; observer.unobserve(state.figure); }
      else observer.observe(state.figure);
      arm(state);
    }
  }
  touch.addEventListener('change', sync); reduced.addEventListener('change', sync);
  document.addEventListener('visibilitychange', () => { for (const state of states.values()) arm(state); });
  document.addEventListener('shopify:section:load', event => scan(event.target));
  window.addEventListener('pagehide', () => { for (const state of states.values()) cancel(state); });
  window.addEventListener('pageshow', sync);
  scan();
  if (typeof MutationObserver !== 'undefined') new MutationObserver(records => {
    // Facet results, recommendations and variant media can replace existing cards.
    for (const record of records) for (const node of record.addedNodes) if (node.querySelectorAll) scan(node);
    for (const state of states.values()) {
      if (!state.card.isConnected || state.primary !== state.card.querySelector('.product-card__image--primary') || state.secondary !== state.card.querySelector('.product-card__image--secondary')) remove(state);
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
